"use client";

import { AuthView } from "better-auth-ui";
import type { ComponentProps, ReactNode } from "react";

import { Card } from "@repo/design-system/components/card";
import { Skeleton } from "@repo/design-system/components/skeleton";
import { useHydrationGuard } from "@repo/hooks";

type AuthViewClassNames = ComponentProps<typeof AuthView>["classNames"];

export type AuthCardProps = {
  /**
   * Render the form without the default surrounding Card. Use when the caller
   * supplies its own surface (e.g. a split-panel auth layout).
   */
  readonly bare?: boolean;
  /** Optional node rendered above the form (e.g. a branded heading). */
  readonly cardHeader?: ReactNode;
  /** Forwarded to better-auth-ui's AuthView for per-element theming. */
  readonly classNames?: AuthViewClassNames;
  /** The `[authView]` route segment, e.g. "sign-in" | "sign-up" | "forgot-password". */
  readonly pathname?: string;
  /**
   * Legacy explicit view selector. Prefer `pathname` so better-auth-ui derives
   * the view (and its internal navigation) from the route segment.
   */
  readonly view?: "FORGOT_PASSWORD" | "SIGN_IN" | "SIGN_UP";
};

export function AuthCard({ bare = false, cardHeader, classNames, pathname, view }: AuthCardProps) {
  // Render the form only after hydration. better-auth-ui's AuthView is a
  // client component that SSR-renders a native <form>: between first paint and
  // hydration a click on "Create an account" falls through to a native GET
  // submit (?name=…&email=…&password=…), so no client signUp fires and the
  // authenticated e2e specs die on waitForURL(/dashboard/) — a hydration
  // timing race, worst on slow/contended CI. Gate on hydration and show a
  // skeleton instead, so there is no unhydrated form to native-submit.
  const hydrated = useHydrationGuard();
  if (!hydrated) {
    return (
      <Skeleton
        className={bare ? undefined : "mx-auto w-full max-w-md"}
        data-testid="auth-card-loading"
      />
    );
  }

  const sharedProps = {
    ...(cardHeader === undefined ? {} : { cardHeader }),
    ...(classNames === undefined ? {} : { classNames }),
  };
  const authView =
    pathname === undefined ? (
      <AuthView {...sharedProps} view={view ?? "SIGN_IN"} />
    ) : (
      <AuthView {...sharedProps} pathname={pathname} />
    );

  if (bare) {
    return <div data-testid="auth-card">{authView}</div>;
  }

  return (
    <Card className="mx-auto w-full max-w-md p-6" data-testid="auth-card">
      {authView}
    </Card>
  );
}
